import { cellAtWorld, cellCenter, heroWalkable } from '../grid.ts';
import { idiv, imax, isqrt, mulBp, withinRange, iatan2 } from '../math/fixed.ts';
import { SENDA } from '@somnhold/content';
import { cellId } from '../grid.ts';
import type { BattleState, CanalRules, CanalState, Enemy, GuardRules, Hero, SendaNpc } from '../types.ts';
import type { CanalLayout } from './canal-layout.ts';
import { hexToWorld } from '../hex.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { attackHero, stepToward } from './enemies.ts';
import { orderMove } from './heroes.ts';
import { guardTick } from './keep.ts';

/**
 * The canals (docs/sendas/paladin.md, chapter 1, acts 1 and 3; §4, «Los canales»). On the Burn's glass shore the first
 * Vigil cut canals from the sea down to the altars' pools; at each fork a stone shuts one mouth and leaves the other
 * (tapped, the hero goes and moves it, standing still `stand` by it). The sea comes back with each wave (seen `warn`
 * before), and the water runs down the inlet a cell every `flow` ticks, along the canal each stone leaves open when it
 * gets there: into a pool (one wave more, `fill` to fill it), or back to the sea. What it ran stays wet `wet`.
 *
 * - A line burnt on the glass (a cría's fire, the Arado's plough) cuts the canal it crosses for `cut`: the water that
 *   gets there spills on the glass and is lost.
 * - The crías never come onto water: the sea's wet rim, a canal still wet, a pool with water in it. A hero standing in
 *   water they wait out at its edge, and their fire goes out on it (it doesn't burn there).
 * - The small shore (act 1): the pool full opens the first altar's eye, and the act ends `lit` later.
 * - The plough's lines strike `lineBp` of the life of a hero stepping on them, once a run (its three lines are one
 *   burn), and cut what canal they cross as long as a cría's.
 * - The water that goes (act 3, the test): rounds of great waves (they get up the canal that climbs to the high pool)
 *   and small ones (only the low); `drop` after the last great one the sea goes down below the high pool, and an open
 *   mouth lets all of it out the way it came; `ebb` after the last small one the water goes for good, and `settle`
 *   later both pools full win it. Not full, the sea comes back for another round with more crías (a band of `more`);
 *   `rounds` gone that way, it is lost. The Arado ploughs three lines at once, its way and one hex either side.
 *
 * Ser Marit keeps by the altar (by the pools in the test) and strikes what comes (battle/keep.ts).
 *
 * Cost: the water is one front a tick (a wave at a time); wet and burnt cells are arrays in step with the canal's few
 * cells; the crías' fire is looked at once as it lands (new zones only). Nothing walks every nightmare against another.
 */

/** The canals as a fight starts (the renderer's scenes build one too: the close's pools full). */
export const canalFresh = (r: CanalRules): CanalState => ({
  pos: [...r.start],
  job: -1,
  held: 0,
  level: r.pools.map(() => 0),
  front: null,
  wet: r.canal.map(() => 0),
  cuts: r.canal.map(() => 0),
  next: r.first,
  big: true,
  n: 0,
  round: 0,
  drop: -1,
  ebb: -1,
  check: -1,
  lit: -1,
  zone: 0,
  swing: 0,
  plow: { id: 0, mode: 0, t: 0, dx: 0, dy: 0, left: 0, next: 0 },
  burn: [],
  burnUntil: [],
  burnRun: [],
  runs: 0,
  stung: [],
  sting: 0,
  on: -1,
  spilt: 0,
  failed: 0,
});

/**
 * A shore's rules from its layout (the fight's builder, campaign/sendas.ts; and the renderer's scenes): its canals and
 * stones in cells of a board of `radius`, SENDA.canal's numbers (SENDA.ebb's for the test), Marit's blows and the
 * Arado's life (in hit points).
 */
export const canalRules = (L: CanalLayout, radius: number, marit: GuardRules, plowHp: number): CanalRules => {
  const id = (h: { q: number; r: number }): number => cellId(radius, h.q, h.r);
  const C = SENDA.canal;
  const E = SENDA.ebb;
  const cells = new Set<number>();
  for (const e of L.edges) for (const c of e.cells) cells.add(id(c));
  return {
    kind: L.kind,
    edges: L.edges.map((e) => ({ cells: e.cells.map(id), to: e.to.kind, n: e.to.n, climb: !!e.climb })),
    inlet: L.inlet,
    stones: L.stones.map((st) => ({ at: id(st.at), out: st.out, mouth: st.mouth ?? -1 })),
    pools: L.pools.map((p) => ({ at: id(p.at), altar: id(p.altar), high: p.high, mouth: p.mouth })),
    canal: [...cells].sort((a, b) => a - b),
    surf: L.surf.map(id).sort((a, b) => a - b),
    keep: hexToWorld(L.marit.q, L.marit.r),
    spawn: L.spawn.map(id),
    start: [...L.start],
    every: C.every,
    warn: C.warn,
    first: C.first,
    flow: C.flow,
    wet: C.wet,
    fill: C.fill,
    stand: C.stand,
    reach: C.reach,
    cut: C.cut,
    lit: C.lit,
    marit,
    ...(L.kind === 'ebb'
      ? {
          ebb: { big: E.big, small: E.small, drop: E.drop, warn: E.warn, ebb: E.ebb, settle: E.settle, rounds: E.rounds, more: E.more },
          plow: { hp: plowHp, at: E.plow.at, every: E.plow.every, warn: E.plow.warn, len: E.plow.len, speed: E.plow.speed, lineBp: E.plow.lineBp, sting: E.plow.sting, dmgBp: E.plow.dmgBp },
        }
      : {}),
  };
};

/** Index of a sorted list's value (−1: not in it). */
const find = (list: readonly number[], v: number): number => {
  let lo = 0;
  let hi = list.length - 1;
  while (lo <= hi) {
    const m = (lo + hi) >> 1;
    const x = list[m] as number;
    if (x === v) return m;
    if (x < v) lo = m + 1;
    else hi = m - 1;
  }
  return -1;
};

const maritOf = (s: BattleState): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === 'marit');

/** Whether a cell is water now: the sea's wet rim, a canal still wet, a pool with water in it. */
export const canalWet = (s: BattleState, cell: number): boolean => {
  const r = s.rules?.senda?.canal;
  const w = s.senda?.canal;
  if (!r || cell < 0) return false;
  if (find(r.surf, cell) >= 0) return true;
  if (!w) return false;
  const i = find(r.canal, cell);
  if (i >= 0 && (w.wet[i] as number) > s.tick) return true;
  for (let p = 0; p < r.pools.length; p++) if ((r.pools[p] as CanalRules['pools'][number]).at === cell && (w.level[p] as number) > 0) return true;
  return false;
};

/** Whether a canal's cell is cut by a burnt line now. */
export const canalCut = (s: BattleState, cell: number): boolean => {
  const r = s.rules?.senda?.canal;
  const w = s.senda?.canal;
  if (!r || !w) return false;
  const i = find(r.canal, cell);
  return i >= 0 && (w.cuts[i] as number) > s.tick;
};

/** The hero sent to a stone (a tap on its mark): it walks there and moves it, standing still by it. */
export const canalOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.canal;
  const w = s.senda?.canal;
  if (!r || !w || at < 0 || at >= r.stones.length) return;
  w.job = at;
  w.held = 0;
  const c = cellCenter(s.map.radius, (r.stones[at] as CanalRules['stones'][number]).at);
  orderMove(s, h, c.x, c.y);
  h.target = 0;
  h.forced = false;
};

/** The hero's hands at the stone: standing still by it `stand`, it moves (a fork's to its other mouth; a mouth open or shut). */
const heroWork = (s: BattleState, r: CanalRules, w: CanalState): void => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h || w.job < 0) return;
  const st = r.stones[w.job] as CanalRules['stones'][number];
  const c = cellCenter(s.map.radius, st.at);
  if (h.moving || !withinRange(h.x, h.y, c.x, c.y, r.reach)) {
    w.held = 0;
    if (!h.moving) w.job = -1;
    return;
  }
  if (++w.held < r.stand) return;
  w.pos[w.job] = 1 - (w.pos[w.job] as number);
  ev(s, { t: 'senda', kind: 'flip', npc: '', x: c.x, y: c.y, n: w.job });
  w.job = -1;
  w.held = 0;
};

/** Wets a canal's cell (the water ran it). */
const wet = (s: BattleState, r: CanalRules, w: CanalState, cell: number): void => {
  const i = find(r.canal, cell);
  if (i >= 0) w.wet[i] = s.tick + r.wet;
};

const spill = (s: BattleState, w: CanalState, cell: number, cue: (c: string) => void): void => {
  const c = cellCenter(s.map.radius, cell);
  ev(s, { t: 'senda', kind: 'spill', npc: '', x: c.x, y: c.y, n: cell });
  w.front = null;
  w.spilt++;
  cue('spill');
};

/** A pool gets a wave (one more, to `fill`); the small shore's opens the eye when full. */
const into = (s: BattleState, r: CanalRules, w: CanalState, p: number, cue: (c: string) => void): void => {
  const pool = r.pools[p] as CanalRules['pools'][number];
  const c = cellCenter(s.map.radius, pool.at);
  if ((w.level[p] as number) < r.fill) w.level[p] = (w.level[p] as number) + 1;
  ev(s, { t: 'senda', kind: 'fill', npc: '', x: c.x, y: c.y, n: p * 16 + (w.level[p] as number) });
  w.front = null;
  if (r.kind === 'small' && (w.level[p] as number) >= r.fill && w.lit < 0) {
    w.lit = s.tick;
    ev(s, { t: 'senda', kind: 'eye', npc: '', x: c.x, y: c.y, n: p });
    cue('lit');
  }
};

/** The wave's front a cell on: cut lines spill it, a fork sends it down its stone's canal, a pool or the sea takes it. */
const advance = (s: BattleState, r: CanalRules, w: CanalState, cue: (c: string) => void): void => {
  const f = w.front;
  if (!f || s.tick < f.t) return;
  const edge = r.edges[f.e] as CanalRules['edges'][number];
  // A small wave can't get up the climb.
  if (f.i === 0 && edge.climb && !f.big) return spill(s, w, edge.cells[0] as number, cue);
  const cell = edge.cells[f.i] as number;
  if (canalCut(s, cell)) return spill(s, w, cell, cue);
  wet(s, r, w, cell);
  ev(s, { t: 'senda', kind: 'flow', npc: '', x: 0, y: 0, n: cell });
  f.t = s.tick + r.flow;
  if (f.i < edge.cells.length - 1) {
    f.i++;
    return;
  }
  if (edge.to === 'sea') {
    w.front = null;
    w.spilt++;
    const c = cellCenter(s.map.radius, cell);
    ev(s, { t: 'senda', kind: 'spill', npc: '', x: c.x, y: c.y, n: -1 });
    cue('sea');
    return;
  }
  if (edge.to === 'pool') return into(s, r, w, edge.n, cue);
  const st = r.stones[edge.n] as CanalRules['stones'][number];
  if (st.mouth >= 0) {
    // The high pool's lip: shut, nothing gets in.
    if (w.pos[edge.n] === 1) return spill(s, w, cell, cue);
    f.e = st.out[0];
  } else f.e = st.out[w.pos[edge.n] as number] as number;
  f.i = 0;
};

/** The waves' plan: the small shore, one every `every`; the test, its rounds of great and small, the drop and the going. */
const tide = (s: BattleState, r: CanalRules, w: CanalState, cue: (c: string) => void): void => {
  const T = s.waveT;
  const E = r.ebb;
  if (w.next >= 0 && T === w.next - r.warn) ev(s, { t: 'senda', kind: 'seaWarn', npc: '', x: 0, y: 0, n: w.big ? 1 : 0 });
  if (w.next >= 0 && T >= w.next) {
    const first = (r.edges[r.inlet] as CanalRules['edges'][number]).cells[0] as number;
    w.front = { e: r.inlet, i: 0, big: w.big, t: s.tick };
    const c = cellCenter(s.map.radius, first);
    ev(s, { t: 'senda', kind: 'wave', npc: '', x: c.x, y: c.y, n: w.big ? 1 : 0 });
    w.n++;
    if (!E) w.next = T + r.every;
    else if (w.n < E.big) w.next = T + r.every;
    else if (w.n === E.big) {
      // The last great one: the sea drops below the high pool a while after; then the small ones.
      w.drop = T + E.drop;
      w.next = T + r.every;
      w.big = false;
    } else if (w.n < E.big + E.small) w.next = T + r.every;
    else {
      w.ebb = T + E.ebb;
      w.next = -1;
    }
  }
  if (!E) return;
  if (w.drop >= 0 && T === w.drop - E.warn) {
    ev(s, { t: 'senda', kind: 'drop', npc: '', x: 0, y: 0, n: 1 });
    cue('drop');
  }
  if (w.drop >= 0 && T >= w.drop) {
    w.drop = -1;
    // An open mouth lets the high pool out the way the water came.
    for (let p = 0; p < r.pools.length; p++) {
      const pool = r.pools[p] as CanalRules['pools'][number];
      if (!pool.high || pool.mouth < 0 || w.pos[pool.mouth] === 1 || (w.level[p] as number) === 0) continue;
      w.level[p] = 0;
      const c = cellCenter(s.map.radius, pool.at);
      ev(s, { t: 'senda', kind: 'drain', npc: '', x: c.x, y: c.y, n: p });
    }
  }
  if (w.ebb >= 0 && T === w.ebb - E.warn) ev(s, { t: 'senda', kind: 'ebb', npc: '', x: 0, y: 0, n: 1 });
  if (w.ebb >= 0 && T >= w.ebb) {
    w.ebb = -1;
    w.check = T + E.settle;
    ev(s, { t: 'senda', kind: 'ebb', npc: '', x: 0, y: 0, n: 0 });
  }
  if (w.check >= 0 && T >= w.check) {
    w.check = -1;
    if (w.level.every((l) => l >= r.fill)) {
      w.lit = s.tick;
      for (let p = 0; p < r.pools.length; p++) {
        const c = cellCenter(s.map.radius, (r.pools[p] as CanalRules['pools'][number]).at);
        ev(s, { t: 'senda', kind: 'eye', npc: '', x: c.x, y: c.y, n: p });
      }
      cue('lit');
      return;
    }
    w.failed++;
    cue(`failed${w.failed}`);
    if (w.failed >= E.rounds) return;
    // The sea comes back: another round, with more crías.
    w.round++;
    w.n = 0;
    w.big = true;
    w.next = T + r.first;
    for (let k = 0; k < E.more + 1; k++) {
      const at = r.spawn[(s.tick + k * 7) % r.spawn.length] as number;
      const c = cellCenter(s.map.radius, at);
      const e = spawnEnemy(s, 'wyrmling', 1, false, 0, c.x + k * 300, c.y);
      if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
    }
  }
};

/** A cría's fire as it lands: out on water (steam), and across a canal it cuts it. */
const fires = (s: BattleState, r: CanalRules, w: CanalState): void => {
  let top = w.zone;
  for (const z of s.zones) {
    if (z.id <= w.zone) continue;
    if (z.id > top) top = z.id;
    if (z.kind !== 'wyrmfire') continue;
    const cell = cellAtWorld(s, z.x, z.y);
    if (canalWet(s, cell)) {
      z.ticks = 0;
      ev(s, { t: 'senda', kind: 'steam', npc: '', x: z.x, y: z.y });
      continue;
    }
    const i = find(r.canal, cell);
    if (i >= 0) w.cuts[i] = imax(w.cuts[i] as number, s.tick + r.cut);
  }
  w.zone = top;
};

/** A burnt cell of the plough (kept sorted, with until when); a canal's is cut as long. */
const burnCell = (s: BattleState, r: CanalRules, w: CanalState, cell: number): void => {
  if (cell < 0 || !heroWalkable(s, cell) || canalWet(s, cell)) return;
  const was = canalBurnt(s, cell);
  const until = s.tick + r.cut;
  let i = 0;
  while (i < w.burn.length && (w.burn[i] as number) < cell) i++;
  if (w.burn[i] === cell) {
    w.burnUntil[i] = until;
    w.burnRun[i] = w.runs;
  } else {
    w.burn.splice(i, 0, cell);
    w.burnUntil.splice(i, 0, until);
    w.burnRun.splice(i, 0, w.runs);
  }
  const k = find(r.canal, cell);
  if (k >= 0) w.cuts[k] = imax(w.cuts[k] as number, until);
  // Burnt under the hero's feet: it steps on it (the sting reads it next).
  if (!was && w.on === cell) w.on = -1;
  // Ser Marit under the plough's line takes it once (she knows the glass: she doesn't stand on it after).
  const m = maritOf(s);
  const P = r.plow;
  if (P && !was && m && m.hp > 0 && cellAtWorld(s, m.x, m.y) === cell) {
    m.hp -= imax(1, idiv(m.maxHp * P.lineBp + 9999, 10000));
    if (m.hp < 0) m.hp = 0;
    ev(s, { t: 'senda', kind: m.hp > 0 ? 'hit' : 'down', npc: m.id, x: m.x, y: m.y });
  }
};

/** Whether the plough's line burns this cell now. */
export const canalBurnt = (s: BattleState, cell: number): boolean => {
  const w = s.senda?.canal;
  if (!w) return false;
  const i = find(w.burn, cell);
  return i >= 0 && (w.burnUntil[i] as number) > s.tick;
};

/** The lines cool off; who stands on a hot one is struck (`lineBp` of its life, once every `sting`). */
const burns = (s: BattleState, r: CanalRules, w: CanalState): void => {
  if (w.burn.length && s.tick % 12 === 0) {
    // Cooled ones off the list.
    for (let i = w.burn.length - 1; i >= 0; i--) {
      if ((w.burnUntil[i] as number) > s.tick) continue;
      w.burn.splice(i, 1);
      w.burnUntil.splice(i, 1);
      w.burnRun.splice(i, 1);
    }
  }
  const P = r.plow;
  const h = s.heroes[0];
  if (!P || !h) return;
  // Stepping onto a hot line (or one burnt under its feet) strikes it, once for each run of the plough: its three lines
  // are one burn. Standing still on one already cooling, not.
  const cell = cellAtWorld(s, h.x, h.y);
  const moved = cell !== w.on;
  w.on = cell;
  if (h.downTicks > 0 || !w.burn.length || s.tick < w.sting || !moved || !canalBurnt(s, cell)) return;
  const run = w.burnRun[find(w.burn, cell)] as number;
  if (w.stung.includes(run)) return;
  w.stung.push(run);
  if (w.stung.length > 4) w.stung.shift();
  damageHero(s, h, mulBp(h.stats.maxHp, P.lineBp), null, 'fire', true);
  w.sting = s.tick + P.sting;
};

/** The Arado comes in from the Burn. */
const plowIn = (s: BattleState, r: CanalRules, w: CanalState): void => {
  const P = r.plow;
  if (!P || w.plow.id || s.waveT < P.at) return;
  const at = r.spawn[(s.tick * 5) % r.spawn.length] as number;
  const c = cellCenter(s.map.radius, at);
  const e = spawnEnemy(s, 'ogre', 1, false, 0, c.x, c.y, P.hp);
  e.damage = imax(1, mulBp(e.damage, P.dmgBp));
  w.plow.id = e.id;
  w.plow.next = s.tick + P.every;
};

/**
 * The Arado's rule (`canalHold`): it walks at the hero and strikes it; every `every` it lowers its blades toward it
 * (`warn`, its three lines marked ahead), then runs `len` hexes straight, burning its way and a hex either side.
 */
const plowMove = (s: BattleState, r: CanalRules, w: CanalState, e: Enemy, pace: number): void => {
  const P = r.plow;
  if (!P) return;
  const p = w.plow;
  if (e.cd > 0) e.cd--;
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (p.mode === 0) {
    if (h && s.tick >= p.next) {
      // Its blades down toward the hero: the lines it will burn are marked.
      const dx = h.x - e.x;
      const dy = h.y - e.y;
      const d = imax(1, isqrt(dx * dx + dy * dy));
      p.dx = idiv(dx * 1000, d);
      p.dy = idiv(dy * 1000, d);
      p.mode = 1;
      p.t = s.tick + P.warn;
      e.facing = iatan2(dy, dx);
      e.anim = 'cast';
      e.animT = 0;
      ev(s, { t: 'senda', kind: 'plowWarn', npc: '', x: e.x, y: e.y, n: e.facing });
      return;
    }
    if (!h) return;
    e.anim = 'walk';
    if (withinRange(e.x, e.y, h.x, h.y, 900)) {
      e.facing = iatan2(h.y - e.y, h.x - e.x);
      if (e.cd === 0) attackHero(s, e, h);
      return;
    }
    stepToward(e, h.x, h.y, pace);
    return;
  }
  if (p.mode === 1) {
    e.anim = 'idle';
    if (s.tick < p.t) return;
    p.mode = 2;
    p.left = idiv(P.len * 1000, P.speed);
    w.runs++;
    ev(s, { t: 'senda', kind: 'plow', npc: '', x: e.x, y: e.y, n: e.facing });
    return;
  }
  // Ploughing: straight on, the three lines burning behind it; what it runs over it strikes.
  e.anim = 'walk';
  const nx = e.x + idiv(p.dx * P.speed, 1000);
  const ny = e.y + idiv(p.dy * P.speed, 1000);
  const cell = cellAtWorld(s, nx, ny);
  if (--p.left <= 0 || !heroWalkable(s, cell)) {
    p.mode = 0;
    p.next = s.tick + P.every;
    e.cd = imax(e.cd, 12);
    return;
  }
  e.x = nx;
  e.y = ny;
  // Its way and one hex to either side, across it.
  burnCell(s, r, w, cell);
  burnCell(s, r, w, cellAtWorld(s, nx - p.dy, ny + p.dx));
  burnCell(s, r, w, cellAtWorld(s, nx + p.dy, ny - p.dx));
  if (h && e.cd === 0 && withinRange(e.x, e.y, h.x, h.y, 800)) attackHero(s, e, h);
};

/**
 * The crías and the Arado on the shore (from the enemies' tick, before their own powers): the Arado by its rule; a cría
 * whose hero stands in water waits at its edge (and doesn't breathe).
 */
export const canalHold = (s: BattleState, e: Enemy, pace: (e: Enemy) => number): boolean => {
  const r = s.rules?.senda?.canal;
  const w = s.senda?.canal;
  if (!r || !w || e.stun > 0) return false;
  if (e.id === w.plow.id) {
    plowMove(s, r, w, e, pace(e));
    return true;
  }
  if (e.kind !== 'wyrmling') return false;
  const h = s.heroes.find((x) => x.downTicks === 0 && withinRange(x.x, x.y, e.x, e.y, 2600));
  if (!h || !canalWet(s, cellAtWorld(s, h.x, h.y))) return false;
  // Hovering at the water's edge, facing the hero.
  e.anim = 'idle';
  e.facing = iatan2(h.y - e.y, h.x - e.x);
  if (withinRange(e.x, e.y, h.x, h.y, 1500)) {
    // Too near the water: back off a little.
    const dx = e.x - h.x;
    const dy = e.y - h.y;
    const d = imax(1, isqrt(dx * dx + dy * dy));
    e.x += idiv(dx * 40, d);
    e.y += idiv(dy * 40, d);
  }
  return true;
};

export const canalTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.canal;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.canal ??= canalFresh(r));
  if (!s.started) return;
  heroWork(s, r, w);
  fires(s, r, w);
  tide(s, r, w, cue);
  advance(s, r, w, cue);
  plowIn(s, r, w);
  burns(s, r, w);
  const m = maritOf(s);
  if (m) w.swing = guardTick(s, m, r.keep, r.marit, w.swing);
  // The first cría in: Marit's line about the water.
  if (!st.cues?.includes('cria') && s.enemies.some((e) => e.kind === 'wyrmling' && e.hp > 0)) cue('cria');
};

/** The small shore: its pool's waves, to the fill. The test: both pools' waves, to both full. */
export const canalGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.canal;
  const w = s.senda?.canal;
  if (!r || !w) return { done: 0, need: 1 };
  let done = 0;
  for (const l of w.level) done += l;
  return { done, need: r.fill * r.pools.length };
};

/** Won the eye open (`lit` after, on the small shore), or both pools full as the water went; lost with the rounds gone. */
export const canalResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.senda?.canal;
  const w = s.senda?.canal;
  if (!r || !w) return null;
  if (w.lit >= 0 && (r.kind !== 'small' || s.tick >= w.lit + r.lit)) return 'won';
  if (r.ebb && w.failed >= r.ebb.rounds) return 'lost';
  return null;
};
